// UserCapabilitiesModal — admin UI for the Capability Overrides layer.
//
// Architecture (matches backend):
//   Backend brain:  services/capabilities.py + routers/capabilities.py
//   This modal:     pure viewport — fetches catalog + overrides, posts
//                   grants/revokes through the gated admin endpoints.
//
// All grants/revokes flow through /api/users/{id}/capabilities which is
// gated by the `permissions.update` registry action — so every change
// here lands in audit_logs automatically.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { X, ShieldCheck, ShieldX, Trash2, Plus, Info } from 'lucide-react';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ar-SA', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function isExpired(iso) {
  if (!iso) return false;
  return new Date(iso).getTime() < Date.now();
}

export default function UserCapabilitiesModal({ user, onClose }) {
  const [catalog, setCatalog] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Grant form state
  const [actionId, setActionId] = useState('');
  const [granted, setGranted] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [reason, setReason] = useState('');

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [cat, caps] = await Promise.all([
        axiosInstance.get('/actions'),
        axiosInstance.get(`/users/${user.id}/capabilities`),
      ]);
      setCatalog(cat.data);
      setOverrides(caps.data?.capabilities || []);
    } catch (err) {
      toast.error('تعذّر تحميل الصلاحيات');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    const handle = setTimeout(refresh, 0);
    return () => clearTimeout(handle);
  }, [refresh]);

  // Flatten catalog → [{key, title, category, roles}]
  const allActions = useMemo(() => {
    if (!catalog?.categories) return [];
    const out = [];
    for (const [, items] of Object.entries(catalog.categories)) {
      for (const it of items) out.push(it);
    }
    return out.sort((a, b) => a.key.localeCompare(b.key));
  }, [catalog]);

  const selectedAction = useMemo(
    () => allActions.find((a) => a.key === actionId) || null,
    [allActions, actionId],
  );

  const handleGrant = async () => {
    if (!actionId) {
      toast.error('اختر إجراءً أولاً');
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.post(`/users/${user.id}/capabilities`, {
        action_id: actionId,
        granted,
        reason: reason || null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      toast.success(granted ? 'تم منح الصلاحية' : 'تم تسجيل التقييد');
      setActionId('');
      setReason('');
      setExpiresAt('');
      setGranted(true);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'تعذّر الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (capId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الاستثناء؟')) return;
    try {
      await axiosInstance.delete(`/users/${user.id}/capabilities/${capId}`);
      toast.success('تم الحذف');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'تعذّر الحذف');
    }
  };

  if (!user) return null;

  // Active = not expired. Sort active first, then expired.
  const sortedOverrides = [...overrides].sort((a, b) => {
    const aExp = isExpired(a.expires_at);
    const bExp = isExpired(b.expires_at);
    if (aExp !== bExp) return aExp ? 1 : -1;
    return (b.granted_at || '').localeCompare(a.granted_at || '');
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      dir="rtl"
      data-testid="capabilities-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
              صلاحيات استثنائية
            </div>
            <div className="text-lg font-semibold text-slate-900">
              {user.full_name || user.username}
            </div>
            <div className="text-sm text-slate-500">
              الدور الأساسي:{' '}
              <span className="font-medium text-slate-700">{user.role}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="capabilities-close"
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 space-y-6">
          {/* Priority note */}
          <div className="flex gap-2 items-start bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <Info size={16} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-medium mb-0.5">ترتيب الأولوية:</div>
              <div className="text-xs text-amber-700">
                admin → revoke صريح → grant استثنائي → دور المستخدم → الشروط الزمنية / الحالة
              </div>
            </div>
          </div>

          {/* Current overrides table */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">
              الاستثناءات الحالية
            </h3>
            {loading ? (
              <div className="space-y-2">
                {[0, 1].map((i) => (
                  <div key={i} className="h-12 rounded bg-slate-100 animate-pulse" />
                ))}
              </div>
            ) : sortedOverrides.length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-6 border border-dashed border-slate-200 rounded-lg">
                لا توجد استثناءات — يعمل بالدور الأساسي فقط
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-sm" data-testid="capabilities-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="px-3 py-2 text-right">الإجراء</th>
                      <th className="px-3 py-2 text-right">النوع</th>
                      <th className="px-3 py-2 text-right">الانتهاء</th>
                      <th className="px-3 py-2 text-right">السبب</th>
                      <th className="px-3 py-2 text-right">تاريخ المنح</th>
                      <th className="px-3 py-2 text-right">إجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOverrides.map((cap) => {
                      const expired = isExpired(cap.expires_at);
                      return (
                        <tr
                          key={cap.id}
                          className={`border-t border-slate-100 ${expired ? 'opacity-50' : ''}`}
                          data-testid={`capability-row-${cap.id}`}
                        >
                          <td className="px-3 py-2 font-mono text-xs">{cap.action_id}</td>
                          <td className="px-3 py-2">
                            {cap.granted ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700">
                                <ShieldCheck size={14} /> منح
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-rose-700">
                                <ShieldX size={14} /> تقييد
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {expired ? (
                              <span className="text-slate-400">منتهٍ</span>
                            ) : (
                              formatDate(cap.expires_at)
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-600">{cap.reason || '—'}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">
                            {formatDate(cap.granted_at)}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => handleRevoke(cap.id)}
                              data-testid={`revoke-${cap.id}`}
                              className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                              title="حذف الاستثناء"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Grant form */}
          <div className="border-t border-slate-200 pt-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Plus size={14} /> إضافة استثناء جديد
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Action selector */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  الإجراء
                </label>
                <select
                  value={actionId}
                  onChange={(e) => setActionId(e.target.value)}
                  data-testid="grant-action-select"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm"
                >
                  <option value="">— اختر إجراءً —</option>
                  {allActions.map((a) => (
                    <option key={a.key} value={a.key}>
                      {a.title} ({a.key})
                    </option>
                  ))}
                </select>
                {selectedAction && (
                  <div className="mt-1 text-xs text-slate-500">
                    أدوار الاعتماد: {selectedAction.roles?.join(', ') || '—'}
                  </div>
                )}
              </div>

              {/* Granted / Revoked */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  النوع
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setGranted(true)}
                    data-testid="grant-type-allow"
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${
                      granted
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-600 border-slate-300'
                    }`}
                  >
                    <ShieldCheck size={14} className="inline ml-1" /> منح
                  </button>
                  <button
                    type="button"
                    onClick={() => setGranted(false)}
                    data-testid="grant-type-deny"
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${
                      !granted
                        ? 'bg-rose-600 text-white border-rose-600'
                        : 'bg-white text-slate-600 border-slate-300'
                    }`}
                  >
                    <ShieldX size={14} className="inline ml-1" /> تقييد
                  </button>
                </div>
              </div>

              {/* Expiry */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  تاريخ الانتهاء (اختياري)
                </label>
                <input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  data-testid="grant-expires"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm"
                />
              </div>

              {/* Reason */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  السبب (للتدقيق)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="grant-reason"
                  placeholder="مثلاً: تفويض مؤقت — مشروع X"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm"
                />
              </div>
            </div>

            <button
              onClick={handleGrant}
              disabled={saving || !actionId}
              data-testid="grant-submit"
              className="mt-4 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              {saving ? 'جاري الحفظ…' : 'حفظ الاستثناء'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
